import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import CommonIssues from "../components/site/CommonIssues";
import CostFactors from "../components/site/CostFactors";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";
import {
  Wrench,
  Search,
  Cpu,
  BatteryCharging,
  Bell,
  Lightbulb,
  RefreshCw,
  ArrowUpCircle,
  Building2,
  Settings2,
} from "lucide-react";

const IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/pnnuoixs_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_57_18%20PM.png";

export default function RepairsUpgrades() {
  useSEO({
    title: "Fire Safety System Repairs & Upgrades Ireland | FSS",
    description:
      "Fault diagnosis, repairs, component replacement, extensions and upgrades for existing fire alarm, emergency lighting and extinguisher systems throughout Ireland.",
    canonicalPath: "/services/repairs-upgrades",
  });

  return (
    <>
      <PageHero
        eyebrow="Repairs & Upgrades"
        title="Fire Safety System Repairs and Upgrades"
        subtitle="For customers with existing systems that need attention — from intermittent faults and damaged devices to full control-panel upgrades. We assess the installed equipment, identify the appropriate work and provide a clear recommendation."
        image={IMG}
        imageAlt="Fire alarm call-point ready for repair or upgrade"
        ctaPrimary={{ to: "/contact", label: "Request a System Assessment" }}
        ctaSecondary={{ to: "/services/maintenance", label: "Planned Maintenance" }}
      />

      <ServiceBody
        includedSubtitle="Repair & upgrade services we provide"
        included={[
          { icon: Search, title: "Fault finding", copy: "Diagnostic investigation of intermittent, persistent or unclear system faults." },
          { icon: Wrench, title: "System troubleshooting", copy: "Structured troubleshooting of alarm, emergency lighting and control gear." },
          { icon: Cpu, title: "Damaged component replacement", copy: "Replacement of damaged devices, sensors, sounders and interfaces." },
          { icon: BatteryCharging, title: "Battery replacement", copy: "Replacement of degraded batteries in alarm panels and emergency lighting." },
          { icon: Bell, title: "Alarm device replacement", copy: "Replacement of faulty detectors, call-points and sounders." },
          { icon: Lightbulb, title: "Emergency lighting repairs", copy: "Repair or replacement of failed luminaires and control gear." },
          { icon: RefreshCw, title: "Extinguisher replacement", copy: "Replacement of extinguishers reaching end of life or unfit for use." },
          { icon: ArrowUpCircle, title: "System extensions", copy: "Extending existing detection into new or altered areas." },
          { icon: Settings2, title: "Additional detection", copy: "Adding detectors, sounders or interfaces where coverage is missing." },
          { icon: Cpu, title: "Control panel upgrades", copy: "Upgrading legacy control panels to modern, supportable equipment." },
          { icon: RefreshCw, title: "Obsolete equipment replacement", copy: "Replacement of end-of-life devices where spares are no longer available." },
          { icon: Building2, title: "Upgrades following alterations", copy: "System changes following building alterations, refits or change of use." },
        ]}
        whoFor={[
          "Buildings with a fault or fault indication on the alarm panel",
          "Sites experiencing frequent false alarms",
          "Premises with damaged devices or luminaires",
          "Systems using older components with limited spare availability",
          "Buildings that have been altered, extended or changed use",
        ]}
        benefits={[
          "A clear picture of the condition of your existing system",
          "Targeted repairs rather than unnecessary full replacements",
          "Structured advice on repair versus upgrade options",
          "Support for legacy systems where practical",
        ]}
        defaultService="Fault or Repair"
      />

      <CommonIssues
        title="Signs your system may need attention"
        intro="If any of the following apply, arranging an inspection is often the sensible next step:"
        items={[
          "A fault or fault light on the control panel",
          "Frequent or unexplained false alarms",
          "Damaged detectors, call-points or sounders",
          "Emergency lighting fittings that do not illuminate on test",
          "Missing or incomplete service records",
          "System age or components approaching end of life",
          "Building alterations, refits or change of use",
          "A previous provider is no longer supporting your system",
          "You have inherited a system with no known service history",
        ]}
      />

      <CostFactors
        title="What affects repair and upgrade cost"
        intro="Repair and upgrade work is highly variable — no two systems are the same. Once we understand the condition of your equipment and the scope of work required, we provide a clear quotation."
        factors={[
          "The manufacturer and model of the existing system",
          "The age and overall condition of the installed equipment",
          "The availability of matching spare components",
          "The number of devices, panels or luminaires affected",
          "Whether a targeted repair or a wider upgrade is more appropriate",
          "Access requirements, working at height or restricted areas",
          "Any building alterations affecting cable routes or coverage",
          "Whether work can be completed in a single visit",
        ]}
      />

      <FAQBlock
        eyebrow="Repairs & upgrades FAQ"
        title="Common questions about existing systems"
        items={[
          { q: "Can FSS maintain a system installed by another provider?", a: "In most cases, yes. We can inspect the installed equipment, confirm the manufacturer and condition, and advise on the appropriate ongoing service." },
          { q: "Can an old fire alarm system be repaired?", a: "It depends on the age, manufacturer and availability of components. Where spares are still available and the system is otherwise sound, a repair is often possible. Where spares are obsolete, an upgrade may be the better option." },
          { q: "Can additional equipment be added to an existing system?", a: "Often yes — depending on the capacity of the existing panel and whether the manufacturer's compatible devices are still available." },
          { q: "What happens when components are obsolete?", a: "If parts are no longer manufactured or supported, we will explain the practical options — this may include a like-for-like replacement, a partial upgrade, or a phased system replacement." },
          { q: "Can a system be upgraded rather than replaced?", a: "In many cases yes. Panel upgrades, adding modern devices and re-using existing cabling where suitable can extend the life of an installation." },
        ]}
        bg="slate"
      />

      <CTABanner
        title="Existing system needs attention?"
        subtitle="Tell us what you are seeing and we will arrange an appropriate assessment."
        primary={{ to: "/contact", label: "Request a System Assessment" }}
        secondary={{ to: "/services/maintenance", label: "Discuss a Maintenance Plan" }}
      />
    </>
  );
}
